/**
 * Web-only card-on-file collection modal for the Free (pay-per-booking)
 * plan. Mirrors WebStripeCheckoutModal.tsx's structure (same lazy-loaded
 * @stripe/react-stripe-js pattern, same getStripe() singleton) but confirms
 * a SetupIntent instead of a PaymentIntent — saving a card for future
 * off-session $2-per-confirmed-booking charges rather than charging now.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { getStripe } from '../services/webStripeCheckout';
import { createBillingSetupIntent, confirmBillingSetupIntent } from '../services/bookingBillingService';

let StripeElementsLib: any = null;
async function loadStripeElementsLib() {
  if (!StripeElementsLib) {
    StripeElementsLib = await import('@stripe/react-stripe-js');
  }
  return StripeElementsLib;
}

export interface CardOnFileModalProps {
  visible: boolean;
  businessId: string;
  businessEmail: string;
  businessName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const SetupForm: React.FC<{
  clientSecret: string;
  setupIntentId: string;
  businessId: string;
  onSuccess: () => void;
  onClose: () => void;
}> = ({ clientSecret, setupIntentId, businessId, onSuccess, onClose }) => {
  const [lib, setLib] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadStripeElementsLib().then((mod) => {
      if (!cancelled) setLib(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!lib) {
    return (
      <View style={styles.loadingState}>
        <ActivityIndicator size="small" color="#007AFF" />
        <Text style={styles.loadingText}>Loading secure card form…</Text>
      </View>
    );
  }

  const { PaymentElement, useStripe, useElements, Elements } = lib;

  const InnerForm: React.FC = () => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSave = async () => {
      if (!stripe || !elements) return;
      setError(null);
      setSubmitting(true);

      const submitResult = await elements.submit();
      if (submitResult?.error) {
        setError(submitResult.error.message || 'Card details are incomplete.');
        setSubmitting(false);
        return;
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Could not save card.');
        setSubmitting(false);
        return;
      }

      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setError('Card setup did not complete. Please try again.');
        setSubmitting(false);
        return;
      }

      // Server re-verifies with Stripe before activating anything — see
      // backend/server.js's /api/billing/setup-intent/confirm.
      const confirmResult = await confirmBillingSetupIntent(businessId, setupIntentId);
      setSubmitting(false);

      if (!confirmResult.success) {
        setError(confirmResult.error || 'Could not activate pay-per-booking billing.');
        return;
      }

      onSuccess();
    };

    return (
      <View style={styles.formInner}>
        <View style={styles.elementHost} dataSet={{ stripeElementHost: '1' } as any}>
          <PaymentElement options={{ layout: 'tabs' }} />
        </View>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <TouchableOpacity
          style={[styles.saveButton, submitting && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={submitting || !stripe || !elements}
          accessibilityRole="button"
          accessibilityLabel="Save card"
        >
          {submitting ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>Save card & activate</Text>
          )}
        </TouchableOpacity>

        <Text style={styles.disclaimer}>
          Secured by Stripe · $2 charged only when BookAPT sends you a confirmed booking
        </Text>
      </View>
    );
  };

  return (
    <Elements
      stripe={getStripe()}
      options={{
        clientSecret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#007AFF' } },
      }}
    >
      <InnerForm />
      <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={submitting}>
        <Text style={styles.cancelButtonText}>Cancel</Text>
      </TouchableOpacity>
    </Elements>
  );
};

const CardOnFileModal: React.FC<CardOnFileModalProps> = ({
  visible,
  businessId,
  businessEmail,
  businessName,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setClientSecret(null);
      setSetupIntentId(null);
      setSetupError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSetupError(null);

    createBillingSetupIntent(businessId, businessEmail, businessName)
      .then((result) => {
        if (cancelled) return;
        if (!result.success || !result.clientSecret || !result.setupIntentId) {
          setSetupError(result.error || 'Unable to start card setup. Please try again.');
          return;
        }
        setClientSecret(result.clientSecret);
        setSetupIntentId(result.setupIntentId);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setSetupError(err?.message || 'Unable to start card setup. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, businessId, businessEmail, businessName]);

  const canRender = useMemo(() => !!clientSecret && !!setupIntentId, [clientSecret, setupIntentId]);

  if (Platform.OS !== 'web') return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.title}>Add a card</Text>
            <TouchableOpacity onPress={onClose} accessibilityLabel="Close">
              <Text style={styles.close}>✕</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.subtitle}>
            Pay per booking — $2 only when BookAPT sends you a confirmed customer
          </Text>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {loading ? (
              <View style={styles.loadingState}>
                <ActivityIndicator size="large" color="#007AFF" />
                <Text style={styles.loadingText}>Preparing secure card form…</Text>
              </View>
            ) : setupError ? (
              <View style={styles.errorState}>
                <Text style={styles.errorText}>{setupError}</Text>
                <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
                  <Text style={styles.cancelButtonText}>Close</Text>
                </TouchableOpacity>
              </View>
            ) : canRender ? (
              <SetupForm
                clientSecret={clientSecret!}
                setupIntentId={setupIntentId!}
                businessId={businessId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            ) : null}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '90%',
    backgroundColor: 'white',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
  },
  title: { fontSize: 18, fontWeight: '700', color: '#1C1C1E' },
  close: { fontSize: 22, color: '#8E8E93', paddingHorizontal: 8 },
  subtitle: {
    paddingHorizontal: 20,
    paddingBottom: 12,
    color: '#3A3A3C',
    fontSize: 14,
    fontWeight: '500',
  },
  body: { paddingHorizontal: 20 },
  bodyContent: { paddingBottom: 20 },
  loadingState: { paddingVertical: 32, alignItems: 'center', gap: 12 },
  loadingText: { color: '#6C6C70', fontSize: 14 },
  errorState: { paddingVertical: 16 },
  errorText: { color: '#B42318', fontSize: 13, marginVertical: 8 },
  formInner: { gap: 16 },
  elementHost: { paddingVertical: 4 },
  saveButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: { opacity: 0.6 },
  saveButtonText: { color: 'white', fontWeight: '700', fontSize: 16 },
  disclaimer: { fontSize: 11, color: '#8E8E93', textAlign: 'center' },
  cancelButton: {
    marginTop: 12,
    paddingVertical: 10,
    alignItems: 'center',
  },
  cancelButtonText: { color: '#007AFF', fontSize: 14, fontWeight: '600' },
});

export default CardOnFileModal;
